/**
 * Treasury Analysis & Prediction Tab (PM 24/02/2026)
 * ==================================================
 * Analyse & Prédiction financière avancée :
 *   Bloc 1 : Graphique 12 mois — Nb Ops × CA × Coûts × Marge × Solde
 *   Bloc 2 : Diagnostic BFR (DSO, DPO, BFR, ratio sécurité)
 *   Bloc 3 : Prédiction M+1/M+2/M+3 avec 5 scénarios
 *   Bloc 4 : Paramètres (coussin sécurité, seuil, charges fixes)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle,
  Loader2, RefreshCw, Settings2, Save, Target, Wallet, Activity,
  Calendar, DollarSign, ShieldCheck, Layers, LineChart as LineIcon,
  Sliders, Send, Sparkles, Bot, MessageCircle,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0';
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(2)}Md`;
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return Math.round(v).toLocaleString('fr-FR');
};
const fmtExact = (v) => (v === null || v === undefined || isNaN(v))
  ? '0'
  : Math.round(v).toLocaleString('fr-FR');

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const monthLabel = (p) => {
  if (!p) return '';
  const [y, m] = p.split('-');
  return `${MONTHS_FR[+m - 1] || m} ${y.slice(2)}`;
};

const HealthPill = ({ health, label }) => {
  const styles = {
    safe: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    warning: 'bg-amber-100 text-amber-800 border-amber-300',
    danger: 'bg-rose-100 text-rose-800 border-rose-300',
  };
  const icon = health === 'safe' ? CheckCircle2 : health === 'warning' ? AlertTriangle : XCircle;
  const Icon = icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${styles[health] || styles.warning}`}>
      <Icon className="h-3 w-3" />
      {label || (health === 'safe' ? 'Sûr' : health === 'warning' ? 'Vigilance' : 'Alerte')}
    </span>
  );
};

const KpiTile = ({ label, value, subline, icon: Icon, color = 'blue', testId }) => {
  const colors = {
    blue: 'from-blue-50 border-blue-200 text-blue-700',
    emerald: 'from-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'from-amber-50 border-amber-200 text-amber-700',
    rose: 'from-rose-50 border-rose-200 text-rose-700',
    violet: 'from-violet-50 border-violet-200 text-violet-700',
    slate: 'from-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-lg border p-3 bg-gradient-to-b to-white ${colors[color] || colors.blue}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold">
        {Icon && <Icon className="h-3 w-3" />}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-lg font-bold text-zinc-900 mt-0.5 font-mono">{value}</div>
      {subline && <div className="text-[10px] text-slate-500 mt-0.5">{subline}</div>}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
      <div className="font-semibold text-slate-700 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-4 py-0.5" style={{ color: p.color }}>
          <span>{p.name}</span>
          <span className="font-mono font-semibold">
            {p.dataKey === 'opsCount' ? p.value : `${fmtExact(p.value)} XOF`}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */

// ─────────────── What-If Simulator ─────────────── (PM 24/02/2026)
const WhatIfSliderRow = ({ label, value, onChange, min, max, step = 1, unit = '', testId, help }) => (
  <div className="flex flex-col gap-1" data-testid={testId}>
    <div className="flex items-baseline justify-between">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <span className="text-sm font-mono font-bold text-emerald-700">
        {typeof value === 'number' ? fmt(value) : value}{unit ? ` ${unit}` : ''}
      </span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(+e.target.value)}
      className="w-full accent-emerald-600"
    />
    <div className="flex justify-between text-[10px] text-slate-400">
      <span>{fmt(min)}{unit ? unit : ''}</span>
      <span>{help}</span>
      <span>{fmt(max)}{unit ? unit : ''}</span>
    </div>
  </div>
);

const WhatIfSimulator = ({ baseline, avgOps, settings }) => {
  // État initial = baseline
  const [nbOps, setNbOps] = useState(Math.max(1, Math.round(avgOps || 10)));
  const [dsoCible, setDsoCible] = useState(baseline?.dso ?? 45);
  const [dpoNego, setDpoNego] = useState(baseline?.dpo ?? 30);
  const [fixedCharges, setFixedCharges] = useState(baseline?.fixed_charges_monthly ?? 0);
  const [safetyPct, setSafetyPct] = useState(settings?.safety_buffer_pct ?? 15);

  // AI analysis state (PM 24/02/2026)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiQuestionOpen, setAiQuestionOpen] = useState(false);

  const reset = () => {
    setNbOps(Math.max(1, Math.round(avgOps || 10)));
    setDsoCible(baseline?.dso ?? 45);
    setDpoNego(baseline?.dpo ?? 30);
    setFixedCharges(baseline?.fixed_charges_monthly ?? 0);
    setSafetyPct(settings?.safety_buffer_pct ?? 15);
    setAiResult(null);
  };

  const runAiAnalysis = async (question = null) => {
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await axios.post(
        `${API}/treasury/forecast/ai-analyze`,
        {
          baseline,
          simulator_state: {
            nb_ops_m1: nbOps,
            dso_cible: dsoCible,
            dpo_nego: dpoNego,
            fixed_charges: fixedCharges,
            safety_pct: safetyPct,
            simulated_treasury_end: Math.round(treasuryEnd),
            simulated_ratio: parseFloat(ratio.toFixed(2)),
          },
          include_timeseries: true,
          user_question: question,
        },
        { timeout: 60000 },
      );
      setAiResult(data);
      toast({ title: 'Analyse IA terminée', description: `Score de risque : ${data.risk_score}/100 (${data.risk_label})` });
    } catch (e) {
      toast({
        title: 'Erreur analyse IA',
        description: e?.response?.data?.detail || e?.message || 'Échec',
        variant: 'destructive',
      });
    } finally {
      setAiLoading(false);
    }
  };

  // Calcul temps réel (miroir de la formule backend, scenario "central" simple sans saisonnalité)
  const opsFactor = avgOps > 0 ? nbOps / avgOps : 1;
  const caPrevu = (baseline?.avg_revenue_3m || 0) * opsFactor;
  const coutPrevu = (baseline?.avg_cost_3m || 0) * opsFactor;
  const fractionEnc = Math.max(0, Math.min(1, 30 / Math.max(dsoCible, 1)));
  const fractionDec = Math.max(0, Math.min(1, 30 / Math.max(dpoNego, 1)));
  const encaissements = caPrevu * fractionEnc;
  const decaissements = coutPrevu * fractionDec + fixedCharges;
  const treasuryStart = baseline?.treasury_now || 0;
  const treasuryEnd = treasuryStart + encaissements - decaissements;
  const besoinSecu = fixedCharges * (1 + safetyPct / 100);
  const ratio = besoinSecu > 0 ? treasuryEnd / besoinSecu : 999;

  const minRatio = settings?.min_ratio_alert ?? 1.2;
  let health = 'safe';
  if (ratio < minRatio) health = 'danger';
  else if (ratio < minRatio + 0.3) health = 'warning';

  // Deltas vs baseline
  const baseFixed = baseline?.fixed_charges_monthly || 0;
  const baseBesoinSecu = baseFixed * (1 + safetyPct / 100);
  const baseTreasuryEnd = treasuryStart
    + (baseline?.avg_revenue_3m || 0) * Math.max(0, Math.min(1, 30 / Math.max(baseline?.dso || 45, 1)))
    - ((baseline?.avg_cost_3m || 0) * Math.max(0, Math.min(1, 30 / Math.max(baseline?.dpo || 30, 1))) + baseFixed);
  const deltaTreasury = treasuryEnd - baseTreasuryEnd;

  const SliderRow = WhatIfSliderRow;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analysis-block-whatif">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Sliders className="h-4 w-4 text-emerald-600" /> Simulateur What-if
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Ajustez les leviers pour explorer l&apos;impact temps réel sur votre trésorerie M+1
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => runAiAnalysis(null)}
            disabled={aiLoading}
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 disabled:opacity-60 flex items-center gap-1.5 shadow-sm"
            data-testid="whatif-ai-analyze-btn"
          >
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {aiLoading ? 'Analyse IA en cours…' : 'Analyser avec l\'IA'}
          </button>
          <button
            onClick={reset}
            className="px-3 py-1.5 rounded-md text-[11px] font-medium bg-slate-100 hover:bg-slate-200 flex items-center gap-1"
            data-testid="whatif-reset-btn"
          >
            <RefreshCw className="h-3 w-3" /> Réinitialiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sliders */}
        <div className="space-y-4">
          <SliderRow
            label="Nb opérations M+1" value={nbOps} onChange={setNbOps}
            min={1} max={Math.max(200, Math.round((avgOps || 10) * 4))}
            testId="whatif-slider-ops"
            help={`base : ${Math.round(avgOps || 0)}/mois`}
          />
          <SliderRow
            label="DSO cible (jours encaissement)" value={dsoCible} onChange={setDsoCible}
            min={5} max={120} unit="j" testId="whatif-slider-dso"
            help={`base : ${baseline?.dso}j`}
          />
          <SliderRow
            label="DPO négocié (jours paiement transporteurs)" value={dpoNego} onChange={setDpoNego}
            min={5} max={120} unit="j" testId="whatif-slider-dpo"
            help={`base : ${baseline?.dpo}j`}
          />
          <SliderRow
            label="Charges fixes mensuelles" value={fixedCharges} onChange={setFixedCharges}
            min={0} max={Math.max(20000000, (baseline?.avg_revenue_3m || 0))}
            step={100000} testId="whatif-slider-fixed"
            help={`base : ${fmt(baseline?.fixed_charges_monthly)}`}
          />
          <SliderRow
            label="Coussin sécurité" value={safetyPct} onChange={setSafetyPct}
            min={0} max={100} unit="%" testId="whatif-slider-safety"
            help={`base : ${settings?.safety_buffer_pct}%`}
          />
        </div>

        {/* Résultat temps réel */}
        <div className="space-y-3">
          <div className={`rounded-lg border-2 p-4 ${
            health === 'safe' ? 'border-emerald-300 bg-emerald-50' :
            health === 'warning' ? 'border-amber-300 bg-amber-50' :
            'border-rose-300 bg-rose-50'
          }`} data-testid="whatif-result-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-600">Trésorerie fin M+1 simulée</span>
              <HealthPill health={health} label={`${ratio.toFixed(2)}×`} />
            </div>
            <div className="text-2xl font-mono font-bold text-slate-900" data-testid="whatif-treasury-end">{fmt(treasuryEnd)}</div>
            <div className="text-xs text-slate-500 mt-0.5">XOF · vs baseline
              <span className={`ml-1 font-semibold ${deltaTreasury >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                    data-testid="whatif-delta">
                {deltaTreasury >= 0 ? '+' : ''}{fmt(deltaTreasury)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">CA prévu</div>
              <div className="text-sm font-mono font-semibold text-slate-800">{fmt(caPrevu)}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">Coût transp.</div>
              <div className="text-sm font-mono font-semibold text-slate-800">{fmt(coutPrevu)}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">Encaissements</div>
              <div className="text-sm font-mono font-semibold text-emerald-700">+{fmt(encaissements)}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">Décaissements</div>
              <div className="text-sm font-mono font-semibold text-rose-700">−{fmt(decaissements)}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-2 col-span-2">
              <div className="text-[10px] uppercase text-slate-500">Besoin sécurité minimum</div>
              <div className="text-sm font-mono font-semibold text-slate-800">{fmt(besoinSecu)}</div>
            </div>
          </div>

          <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-2">
            <strong>Astuce :</strong> Augmentez le DPO négocié ou baissez le DSO pour améliorer directement
            votre ratio sans changer votre volume d&apos;opérations. Les leviers commerciaux (Nb Ops)
            et opérationnels (Charges fixes) ont un impact plus long à activer.
          </div>
        </div>
      </div>

      {/* ─── AI ANALYSIS RESULT ─── */}
      {(aiResult || aiLoading) && (
        <div className="mt-4 border-t-2 border-violet-100 pt-4" data-testid="whatif-ai-result">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-gradient-to-br from-violet-500 to-purple-600">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">
                Analyse IA — Claude Sonnet 4.5
              </div>
              {aiResult && (
                <div className="text-[10px] text-slate-500">
                  Généré {new Date(aiResult.generated_at).toLocaleTimeString('fr-FR')} · Modèle : {aiResult.model}
                </div>
              )}
            </div>
            {aiResult && (
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                aiResult.risk_score < 25 ? 'bg-emerald-100 text-emerald-800' :
                aiResult.risk_score < 50 ? 'bg-amber-100 text-amber-800' :
                aiResult.risk_score < 75 ? 'bg-orange-100 text-orange-800' :
                'bg-rose-100 text-rose-800'
              }`} data-testid="ai-risk-score">
                Risque {aiResult.risk_score}/100 · {aiResult.risk_label}
              </div>
            )}
          </div>

          {aiLoading && (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-violet-600 mb-2" />
              <div className="text-sm font-medium">L&apos;IA analyse votre scénario…</div>
              <div className="text-xs text-slate-400 mt-1">Cela prend 5-15 secondes</div>
            </div>
          )}

          {aiResult && (
            <>
              {/* Executive Summary */}
              <div className="p-3 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg mb-3"
                   data-testid="ai-executive-summary">
                <div className="text-[10px] uppercase tracking-wide font-semibold text-violet-700 mb-1">
                  📋 Résumé exécutif
                </div>
                <p className="text-sm text-slate-800 leading-relaxed">{aiResult.executive_summary}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Insights */}
                {aiResult.insights?.length > 0 && (
                  <div className="border border-slate-200 rounded-lg p-3" data-testid="ai-insights">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-600 mb-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Insights ({aiResult.insights.length})
                    </div>
                    <div className="space-y-2">
                      {aiResult.insights.map((ins, idx) => (
                        <div key={idx} className={`p-2 rounded border-l-4 ${
                          ins.severity === 'danger' ? 'border-rose-500 bg-rose-50' :
                          ins.severity === 'warning' ? 'border-amber-500 bg-amber-50' :
                          'border-blue-500 bg-blue-50'
                        }`} data-testid={`ai-insight-${idx}`}>
                          <div className="text-xs font-semibold text-slate-800">{ins.key}</div>
                          <div className="text-xs text-slate-700 mt-0.5 leading-snug">{ins.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {aiResult.recommendations?.length > 0 && (
                  <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/50" data-testid="ai-recommendations">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 mb-2 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Recommandations ({aiResult.recommendations.length})
                    </div>
                    <div className="space-y-2">
                      {aiResult.recommendations.map((rec, idx) => (
                        <div key={idx} className="p-2 bg-white rounded border border-emerald-100"
                             data-testid={`ai-recommendation-${idx}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                              rec.priority === 'P0' ? 'bg-rose-600 text-white' :
                              rec.priority === 'P1' ? 'bg-amber-500 text-white' :
                              'bg-slate-400 text-white'
                            }`}>{rec.priority}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                              rec.effort === 'S' ? 'border-emerald-300 text-emerald-700' :
                              rec.effort === 'M' ? 'border-amber-300 text-amber-700' :
                              'border-rose-300 text-rose-700'
                            }`}>Effort {rec.effort}</span>
                            <div className="text-xs font-semibold text-slate-800 flex-1">{rec.title}</div>
                          </div>
                          <div className="text-[11px] text-slate-600 leading-snug mb-1">{rec.why}</div>
                          <div className="text-[11px] text-emerald-700 font-semibold">💰 {rec.expected_impact}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Refined Prediction */}
              {aiResult.refined_prediction && (
                <div className="mt-3 p-3 border-2 border-violet-300 bg-violet-50 rounded-lg"
                     data-testid="ai-refined-prediction">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-violet-700 mb-2 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Prédiction affinée par l&apos;IA
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div>
                      <div className="text-[10px] text-slate-500">Trésorerie fin M+1 ajustée</div>
                      <div className="text-lg font-mono font-bold text-slate-900">
                        {fmt(aiResult.refined_prediction.adjusted_treasury_end)} XOF
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500">Ratio ajusté</div>
                      <div className="text-lg font-mono font-bold text-slate-900">
                        {aiResult.refined_prediction.adjusted_ratio}×
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500">Confiance IA</div>
                      <div className="text-sm font-semibold text-slate-800 capitalize">
                        {aiResult.refined_prediction.confidence}
                      </div>
                    </div>
                  </div>
                  {aiResult.refined_prediction.assumptions?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-violet-200">
                      <div className="text-[10px] uppercase text-violet-600 font-semibold mb-1">Hypothèses de l&apos;IA</div>
                      <ul className="text-xs text-slate-700 list-disc ml-4 space-y-0.5">
                        {aiResult.refined_prediction.assumptions.map((a, i) => (
                          <li key={i}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Question box (facultatif) */}
              <div className="mt-3">
                {!aiQuestionOpen ? (
                  <button
                    onClick={() => setAiQuestionOpen(true)}
                    className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"
                    data-testid="ai-question-open-btn"
                  >
                    <MessageCircle className="h-3 w-3" /> Poser une question spécifique à l&apos;IA
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={aiQuestion}
                      onChange={(e) => setAiQuestion(e.target.value)}
                      placeholder="Ex : Est-ce que je peux embaucher 2 chauffeurs de plus le mois prochain ?"
                      className="flex-1 text-xs border border-slate-200 rounded-md px-3 py-2"
                      data-testid="ai-question-input"
                      onKeyDown={(e) => { if (e.key === 'Enter' && aiQuestion.trim()) runAiAnalysis(aiQuestion); }}
                    />
                    <button
                      onClick={() => aiQuestion.trim() && runAiAnalysis(aiQuestion)}
                      disabled={aiLoading || !aiQuestion.trim()}
                      className="px-3 py-2 rounded-md text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                      data-testid="ai-question-submit-btn"
                    >
                      Envoyer
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */

const TreasuryAnalysisTab = () => {
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [timeseries, setTimeseries] = useState({ months: [], series: [] });
  const [bfr, setBfr] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [fixedCharges, setFixedCharges] = useState(null);
  const [settings, setSettings] = useState(null);
  const [caView, setCaView] = useState('consolidated'); // spot | contract | consolidated
  const [horizon, setHorizon] = useState(3);
  const [tsMonths, setTsMonths] = useState(12);

  // Settings form
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tsRes, bfrRes, fcRes, fcastRes, settRes] = await Promise.allSettled([
        axios.get(`${API}/treasury/timeseries`, { params: { months: tsMonths } }),
        axios.get(`${API}/treasury/bfr`),
        axios.get(`${API}/treasury/fixed-charges`, { params: { months: 3 } }),
        axios.get(`${API}/treasury/forecast`, { params: { horizon } }),
        axios.get(`${API}/treasury/forecast/settings`),
      ]);
      if (tsRes.status === 'fulfilled') setTimeseries(tsRes.value.data);
      if (bfrRes.status === 'fulfilled') setBfr(bfrRes.value.data);
      if (fcRes.status === 'fulfilled') setFixedCharges(fcRes.value.data);
      if (fcastRes.status === 'fulfilled') setForecast(fcastRes.value.data);
      if (settRes.status === 'fulfilled') {
        setSettings(settRes.value.data);
        setSettingsForm(settRes.value.data);
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Chargement analyse échoué', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tsMonths, horizon]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await axios.patch(`${API}/treasury/forecast/settings`, settingsForm);
      setSettings(data);
      toast({ title: 'Paramètres enregistrés' });
      setSettingsOpen(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading && !timeseries.series.length) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analysis-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // ─── Bloc 1 : chart data ─────────────────────────────────────────────
  const chartData = (timeseries.series || []).map(s => ({
    label: monthLabel(s.period),
    period: s.period,
    opsCount: s.opsCount,
    revenue: caView === 'spot' ? s.revenueSpot : caView === 'contract' ? s.revenueContract : s.revenueConsolidated,
    carrierCost: caView === 'spot' ? s.carrierCost : caView === 'contract' ? s.carrierCostContract : s.carrierCostConsolidated,
    margin: caView === 'spot' ? s.grossMarginSpot : caView === 'contract' ? s.grossMarginContract : s.grossMarginConsolidated,
    closingBalance: s.closingBalance,
  }));

  // ─── Bloc 3 : forecast table pivot ──────────────────────────────────
  const scenarioOrder = ['pessimistic', 'low', 'central', 'high', 'optimistic'];
  const forecastByPeriod = {};
  (forecast?.predictions || []).forEach(p => {
    if (!forecastByPeriod[p.period]) forecastByPeriod[p.period] = {};
    forecastByPeriod[p.period][p.scenario] = p;
  });
  const futurePeriods = Object.keys(forecastByPeriod);

  return (
    <div className="space-y-6" data-testid="treasury-analysis-tab">
      {/* Header actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600" />
            Analyse & Prédiction Trésorerie
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Comprendre l&apos;impact Nb Ops × CA × Marge × Trésorerie · Prédire les {horizon} prochains mois
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 flex items-center gap-1.5"
            data-testid="analysis-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Actualiser
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1.5"
            data-testid="analysis-settings-btn"
          >
            <Settings2 className="h-3.5 w-3.5" /> Profil de sécurité
          </button>
        </div>
      </div>

      {/* BLOC 1 : Évolution multi-métriques */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analysis-block-timeseries">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <LineIcon className="h-4 w-4 text-emerald-600" />
              Évolution {tsMonths} mois — Ops × CA × Coûts × Marge × Solde
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Barres : CA (empilé Coûts + Marge) · Lignes : Nb Ops (droite) et Solde bancaire
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="ca-view-selector">
              {[
                { k: 'spot', l: 'Spot' },
                { k: 'contract', l: 'Contrats' },
                { k: 'consolidated', l: 'Consolidé' },
              ].map(v => (
                <button
                  key={v.k}
                  onClick={() => setCaView(v.k)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                    caView === v.k ? 'bg-white shadow text-emerald-700' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`ca-view-${v.k}`}
                >
                  {v.l}
                </button>
              ))}
            </div>
            <select
              value={tsMonths}
              onChange={(e) => setTsMonths(+e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1"
              data-testid="ts-months-selector"
            >
              <option value={6}>6 mois</option>
              <option value={12}>12 mois</option>
              <option value={24}>24 mois</option>
            </select>
          </div>
        </div>
        <div className="h-72" data-testid="analysis-timeseries-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} />
              <YAxis yAxisId="left" tickFormatter={fmt} tick={{ fontSize: 10, fill: '#64748b' }} width={55} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="carrierCost" stackId="ca" fill="#3b82f6" name="Coûts Transporteurs" />
              <Bar yAxisId="left" dataKey="margin" stackId="ca" fill="#10b981" name="Marge Brute" />
              <Line yAxisId="left" type="monotone" dataKey="closingBalance" stroke="#8b5cf6" strokeWidth={2}
                    name="Solde bancaire fin de mois" dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="opsCount" stroke="#f59e0b" strokeWidth={2}
                    name="Nb Opérations" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* BLOC 2 : Diagnostic BFR */}
      {bfr && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analysis-block-bfr">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Target className="h-4 w-4 text-emerald-600" /> Diagnostic BFR — Situation actuelle
            </h3>
            <HealthPill health={bfr.health} label={
              bfr.health === 'safe' ? 'Trésorerie sécurisée' :
              bfr.health === 'warning' ? 'À surveiller' : 'Alerte trésorerie'
            } />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <KpiTile label="DSO" value={`${bfr.dso} j`}
              subline={`Encaissement clients · ${bfr.dso_samples} obs.`}
              icon={Calendar} color="blue" testId="bfr-dso" />
            <KpiTile label="DPO" value={`${bfr.dpo} j`}
              subline={`Paiement transp. · ${bfr.dpo_samples} obs.`}
              icon={Calendar} color="violet" testId="bfr-dpo" />
            <KpiTile label="BFR" value={fmt(bfr.bfr)}
              subline={`Créances ${fmt(bfr.bfr_creances)} − Dettes ${fmt(bfr.bfr_dettes)}`}
              icon={Wallet} color="amber" testId="bfr-total" />
            <KpiTile label="Cash / Opération"
              value={fmt(bfr.cash_per_op)}
              subline={`Sur ${bfr.avg_ops_per_month} ops/mois moy.`}
              icon={Layers} color="emerald" testId="bfr-cash-per-op" />
            <KpiTile label="Trésorerie dispo"
              value={fmt(bfr.treasury_available)}
              subline="Bank + Wave consolidé"
              icon={DollarSign} color="emerald" testId="bfr-treasury" />
            <KpiTile label="Ratio sécurité"
              value={`${bfr.ratio}×`}
              subline={bfr.ratio >= 1.5 ? '≥ 1.5 (sûr)' : bfr.ratio >= 1 ? '1–1.5 (vigilance)' : '< 1 (danger)'}
              icon={ShieldCheck}
              color={bfr.health === 'safe' ? 'emerald' : bfr.health === 'warning' ? 'amber' : 'rose'}
              testId="bfr-ratio" />
          </div>

          <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 leading-relaxed">
            <strong>Interprétation :</strong> Pour maintenir votre activité au rythme actuel de{' '}
            <strong>{bfr.avg_ops_per_month} opérations/mois</strong> (CA moyen {fmt(bfr.avg_revenue_per_month)} XOF),
            vous devez immobiliser en moyenne <strong>{fmt(bfr.bfr)} XOF</strong> de trésorerie
            (soit <strong>{fmt(bfr.cash_per_op)} XOF par opération</strong>). Votre trésorerie disponible couvre{' '}
            <strong>{bfr.ratio}×</strong> ce besoin.
          </div>
        </div>
      )}

      {/* BLOC 3 : Prédiction */}
      {forecast && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analysis-block-forecast">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" /> Prédiction Trésorerie
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Modèle : moyenne mobile 3M × saisonnalité ·
                CA moyen 3M : <strong>{fmt(forecast.baseline.avg_revenue_3m)}</strong> XOF ·
                Charges fixes M+1 : <strong>{fmt(forecast.baseline.fixed_charges_monthly)}</strong> XOF{' '}
                ({forecast.baseline.fixed_source === 'auto' ? 'auto-détecté' : 'manuel'})
              </p>
            </div>
            <select
              value={horizon}
              onChange={(e) => setHorizon(+e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1"
              data-testid="horizon-selector"
            >
              <option value={1}>M+1</option>
              <option value={2}>M+1 · M+2</option>
              <option value={3}>M+1 · M+2 · M+3</option>
              <option value={6}>6 mois</option>
              <option value={12}>12 mois</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="forecast-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
                  <th className="text-left py-2 px-2 font-semibold">Scénario</th>
                  {futurePeriods.map(pk => (
                    <th key={pk} className="text-right py-2 px-2 font-semibold">{monthLabel(pk)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scenarioOrder.map(sc => {
                  const first = forecastByPeriod[futurePeriods[0]]?.[sc];
                  if (!first) return null;
                  return (
                    <tr key={sc} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`forecast-row-${sc}`}>
                      <td className="py-2 px-2 font-medium text-slate-800">{first.scenario_label}
                        <div className="text-[10px] text-slate-400 font-normal">
                          {sc === 'pessimistic' ? '−20% CA' : sc === 'low' ? '−10% CA' :
                            sc === 'high' ? '+10% CA' : sc === 'optimistic' ? '+20% CA' : 'CA base'}
                        </div>
                      </td>
                      {futurePeriods.map(pk => {
                        const cell = forecastByPeriod[pk]?.[sc];
                        if (!cell) return <td key={pk} className="py-2 px-2 text-right text-slate-300">—</td>;
                        return (
                          <td key={pk} className="py-2 px-2 text-right" data-testid={`forecast-cell-${sc}-${pk}`}>
                            <div className="font-mono font-semibold text-slate-900">{fmt(cell.tresorerie_fin)} XOF</div>
                            <div className="text-[10px] text-slate-500">
                              CA {fmt(cell.ca_prevu)} · Enc. {fmt(cell.encaissements_prevus)} · Déc. {fmt(cell.decaissements_prevus)}
                            </div>
                            <div className="mt-1"><HealthPill health={cell.health} label={`${cell.ratio}×`} /></div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-slate-700 leading-relaxed">
            <strong>Comment lire :</strong> Chaque cellule montre la <strong>trésorerie prévue en fin de mois</strong>{' '}
            selon le scénario et la ligne de calcul (encaissements CA × 30/DSO − décaissements coûts × 30/DPO − charges fixes).
            Le ratio × sécurité est calculé vs le besoin minimum (charges fixes × coussin sécurité).
            <br />
            Les scénarios <strong>Pessimiste/Bas</strong> aident à identifier <strong>quand ajouter du cash</strong> ;
            les scénarios <strong>Haut/Optimiste</strong> à anticiper les <strong>investissements possibles</strong>.
          </div>
        </div>
      )}

      {/* Charges fixes détectées */}
      {fixedCharges && fixedCharges.categories?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analysis-block-fixed-charges">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Wallet className="h-4 w-4 text-emerald-600" /> Charges fixes détectées (moyenne 3 derniers mois)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {fixedCharges.categories.map(c => (
              <div key={c.category}
                className="border border-slate-200 rounded-lg p-2.5 flex items-center justify-between"
                data-testid={`fixed-charge-${c.category}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-slate-800 truncate">{c.category}</div>
                  <div className="text-[10px] text-slate-500">
                    {c.occurrences}/{fixedCharges.periods_analyzed.length} mois
                    {c.is_known_fixed && <span className="ml-1 text-emerald-600">✓ récurrent</span>}
                  </div>
                </div>
                <div className="text-sm font-mono font-semibold text-slate-900">{fmt(c.avg_monthly)}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-600">
            Total charges fixes mensuelles estimées : <strong className="font-mono">{fmt(fixedCharges.total_fixed_monthly)} XOF</strong>
          </div>
        </div>
      )}

      {/* BLOC 5 : Simulateur What-if */}
      {forecast && bfr && (
        <WhatIfSimulator
          baseline={forecast.baseline}
          avgOps={bfr.avg_ops_per_month}
          settings={settings}
        />
      )}

      {/* Action : Tester l'alerte */}
      {forecast && settings && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2"
             data-testid="analysis-alert-action">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Send className="h-4 w-4 text-emerald-600" /> Notifier le CFO
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Envoyer un email + WhatsApp aux destinataires Trésorerie si un scénario est sous le seuil
              <strong className="ml-1">{settings.min_ratio_alert}×</strong>
              (scénarios évalués : Pessimiste, Bas, Central).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const { data } = await axios.post(`${API}/treasury/forecast/check-alerts`,
                    null, { params: { dry_run: true, horizon } });
                  const n = data.triggers?.length || 0;
                  toast({
                    title: n === 0 ? 'Aucun scénario sous le seuil' : `${n} scénario(s) déclencheraient une alerte`,
                    description: n === 0 ? 'Trésorerie prévisionnelle sécurisée sur tous les scénarios.'
                      : data.triggers.slice(0, 3).map(t => `${t.period} · ${t.scenario_label} · ${t.ratio}×`).join(' · '),
                  });
                } catch (e) {
                  toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
                }
              }}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 flex items-center gap-1.5"
              data-testid="alert-preview-btn"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Prévisualiser (dry run)
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('Confirmer l\'envoi de l\'alerte aux destinataires Trésorerie ?')) return;
                try {
                  const { data } = await axios.post(`${API}/treasury/forecast/check-alerts`,
                    null, { params: { dry_run: false, horizon } });
                  if (data.dispatched) {
                    const n_email = data.recipients?.emails?.length || 0;
                    const n_wa = data.recipients?.phones?.length || 0;
                    toast({ title: 'Alerte envoyée', description: `${n_email} email(s) · ${n_wa} WhatsApp` });
                  } else {
                    toast({ title: 'Aucun envoi', description: data.reason === 'no_trigger' ? 'Aucun scénario sous le seuil' : (data.reason || 'raison inconnue') });
                  }
                } catch (e) {
                  toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
                }
              }}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-1.5"
              data-testid="alert-send-btn"
            >
              <Send className="h-3.5 w-3.5" /> Envoyer l&apos;alerte
            </button>
          </div>
        </div>
      )}

      {/* SETTINGS MODAL */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSettingsOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}
               data-testid="analysis-settings-modal">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" /> Profil de sécurité trésorerie
              </h3>
              <button onClick={() => setSettingsOpen(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Coussin de sécurité (%)
                </label>
                <input
                  type="number" step="1" min="0" max="100"
                  value={settingsForm.safety_buffer_pct ?? 15}
                  onChange={(e) => setSettingsForm(p => ({ ...p, safety_buffer_pct: +e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="setting-safety-buffer"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Marge ajoutée aux charges fixes pour calculer le besoin minimum
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Seuil ratio d&apos;alerte
                </label>
                <input
                  type="number" step="0.1" min="0.5" max="5"
                  value={settingsForm.min_ratio_alert ?? 1.2}
                  onChange={(e) => setSettingsForm(p => ({ ...p, min_ratio_alert: +e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="setting-min-ratio"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Une alerte est levée si le ratio prévu passe sous ce seuil
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Charges fixes manuelles (XOF, optionnel)
                </label>
                <input
                  type="number" step="10000" min="0"
                  value={settingsForm.manual_fixed_charges_override ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSettingsForm(p => ({ ...p, manual_fixed_charges_override: v === '' ? null : +v }));
                  }}
                  placeholder="Laisser vide = auto-détection"
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="setting-manual-fixed"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Si renseigné, remplace la détection automatique
                </p>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setSettingsOpen(false)}
                className="px-4 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200"
                data-testid="setting-cancel-btn">
                Annuler
              </button>
              <button onClick={saveSettings} disabled={savingSettings}
                className="px-4 py-2 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1.5"
                data-testid="setting-save-btn">
                {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasuryAnalysisTab;
